import React, { useRef } from 'react';
import GenericServerError from 'src/components/10-atoms/GenericServerError';
import CustomFlatList from 'src/components/10-atoms/list-views/CustomFlatList';
import PaginationNav from 'src/components/10-atoms/list-views/PaginationNav';
import PaginationSeoLinks from 'src/components/10-atoms/seo/PaginationSeoLinks';
import useEffectWithCtx from 'src/hooks/useEffectUtils/useEffectWithCtx';
import { renderNamedSlot } from 'src/utils/component';
import isDeepEqual from 'src/utils/object/isDeepEqual';
import styleUtils from 'src/utils/styles';
import useListState from './useListState';
function GQLPaginatedList({ LoaderComponent, NextLoaderComponent = LoaderComponent, ErrorComponent, ListEmptyComponent, EndOfListComponent = null, queryState, queryActions, variables, renderItem, style, ...listProps }) {
    const { mode, offset, limit, hasNext, buildPageTo, buildPageUrl } = queryState.pagination;
    const isNavigation = mode === 'navigation';
    const prevVariables = useRef(undefined);
    const prevOffset = useRef(offset);
    const prevLimit = useRef(limit);
    useEffectWithCtx(ctx => {
        const variablesChanged = !isDeepEqual(variables, prevVariables.current);
        const paginationChanged = prevOffset.current !== offset || prevLimit.current !== limit;
        if (variablesChanged || paginationChanged) {
            ctx.queryActions.refresh(variables);
            prevVariables.current = variables;
            prevOffset.current = offset;
            prevLimit.current = limit;
        }
    }, { queryActions }, [variables, offset, limit]);
    const handleRetry = () => {
        queryActions.refresh(variables);
    };
    const handleEndReached = () => {
        queryActions.fetchMore(variables);
    };
    const handleRefresh = () => {
        queryActions.refresh(variables);
    };
    const renderListItem = ({ item, ...props }) => {
        switch (item.type) {
            case 'list_loading':
                return renderNamedSlot(LoaderComponent);
            case 'list_empty':
                return renderNamedSlot(ListEmptyComponent);
            case 'list_error':
                return renderNamedSlot(ErrorComponent || (React.createElement(GenericServerError, { error: queryState.error, onRetry: handleRetry })));
            case 'end_of_list':
                return renderNamedSlot(EndOfListComponent);
            case 'list_data':
                return renderItem ? renderItem({ item: item.data, ...props }) : null;
            default:
                return null;
        }
    };
    const [listState] = useListState(queryState);
    const paginationFooter = isNavigation &&
        queryState.data &&
        queryState.data.totalCount > 0 ? (React.createElement(PaginationNav, { offset: offset, limit: limit, totalCount: queryState.data.totalCount, hasMore: hasNext, buildPageTo: buildPageTo })) : null;
    const scrollFooter = !isNavigation && queryState.data?.hasMore ? NextLoaderComponent : null;
    return (React.createElement(React.Fragment, null,
        isNavigation && (React.createElement(PaginationSeoLinks, { offset: offset, limit: limit, hasMore: hasNext, buildPageUrl: buildPageUrl })),
        React.createElement(CustomFlatList, { initialRenderDelay: 50, data: listState.data, renderItem: renderListItem, style: [styleUtils.containerHeight, style], keyExtractor: item => item.type !== 'list_data'
                ? item.type
                : item.data.id, onEndReached: isNavigation || listState.query.empty ? undefined : handleEndReached, onEndReachedThreshold: 0.5, ListFooterComponent: paginationFooter ?? scrollFooter, refreshing: queryState.refetching, onRefresh: handleRefresh, ...listProps })));
}
export default GQLPaginatedList;
